import { z } from 'zod';
import { CognitoSubSchema, TimestampSchema } from '../common.js';
export const AvatarProfileIdSchema = z.literal('primary');
export const AvatarArtifactActorSchema = z.union([
    CognitoSubSchema,
    z.literal('service:avatar-renderer-worker'),
]);
export const Sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/);
export const Sha256DigestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
export const AvatarVersionedObjectSchema = z
    .object({
    bucket: z.string().min(3).max(63),
    key: z.string().min(1).max(1_024),
    versionId: z
        .string()
        .min(1)
        .max(1_024)
        .refine((value) => value !== 'null'),
    etag: z.string().min(1).max(1_024),
    bytes: z.number().int().positive(),
    sha256: Sha256HexSchema,
    contentType: z.string().min(1).max(255),
})
    .strict();
export const AvatarArtifactProviderSchema = z.literal('latentsync');
export const AvatarRendererIdentitySchema = z
    .object({
    provider: AvatarArtifactProviderSchema,
    rendererVersion: z.string().min(1).max(120),
    rendererImageDigest: Sha256DigestSchema,
    modelVersion: z.literal('1.6'),
    modelDigest: Sha256DigestSchema,
})
    .strict();
export const AvatarArtifactStatusSchema = z.enum([
    'queued',
    'preparing',
    'review',
    'active',
    'retired',
    'failed',
    'deleting',
]);
export const AvatarArtifactSourceSchema = z
    .object({
    agentId: z.string().uuid(),
    mediaId: z.string().uuid(),
    object: AvatarVersionedObjectSchema.extend({
        contentType: z.string().regex(/^video\//),
    }),
})
    .strict();
export const AvatarArtifactManifestObjectSchema = AvatarVersionedObjectSchema.extend({
    contentType: z.literal('application/json'),
});
export const AvatarArtifactPreviewObjectSchema = AvatarVersionedObjectSchema.extend({
    contentType: z.literal('video/mp4'),
});
export const AvatarArtifactQualityReviewSchema = z
    .object({
    decision: z.enum(['approved', 'rejected']),
    reviewedAt: TimestampSchema,
    reviewedBy: CognitoSubSchema,
    sourceSha256: Sha256HexSchema,
    manifestSha256: Sha256HexSchema,
    rendererImageDigest: Sha256DigestSchema,
    modelDigest: Sha256DigestSchema,
    preview: AvatarArtifactPreviewObjectSchema,
    failureCode: z.string().min(1).max(80).optional(),
})
    .strict()
    .superRefine((review, ctx) => {
    if ((review.decision === 'rejected') !== (review.failureCode !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['failureCode'],
            message: 'Only a rejected technical review carries a failure code.',
        });
    }
});
function expectedArtifactPrefix(args) {
    return `tenants/${args.tenantId}/avatar-profile/artifacts/${args.artifactId}/${args.renderer.modelDigest}/`;
}
function validateArtifactScope(artifact, ctx) {
    const prefix = expectedArtifactPrefix(artifact);
    if (artifact.artifactS3Prefix !== prefix) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['artifactS3Prefix'],
            message: 'Prepared avatar artifact prefix does not match its tenant and identity.',
        });
    }
    if (artifact.manifest && artifact.manifest.key !== `${prefix}manifest.json`) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['manifest', 'key'],
            message: 'Prepared avatar manifest must use the exact artifact-scoped key.',
        });
    }
    if (artifact.reviewPreview?.key !== undefined) {
        if (artifact.reviewPreview.key !== `${prefix}review-output.mp4`) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['reviewPreview', 'key'],
                message: 'Prepared avatar review output must use the exact artifact-scoped key.',
            });
        }
        if (artifact.manifest && artifact.reviewPreview.bucket !== artifact.manifest.bucket) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['reviewPreview', 'bucket'],
                message: 'Prepared avatar manifest and review output must use the same bucket.',
            });
        }
    }
    const sourcePrefix = `tenants/${artifact.tenantId}/agents/${artifact.source.agentId}/`;
    if (!artifact.source.object.key.startsWith(sourcePrefix)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['source', 'object', 'key'],
            message: 'Prepared avatar source must belong to the same tenant and source agent.',
        });
    }
}
const AvatarArtifactLegacyRuntimeBindingObjectSchema = z
    .object({
    tenantId: z.string().uuid(),
    profileId: AvatarProfileIdSchema,
    artifactId: z.string().uuid(),
    artifactS3Prefix: z.string().min(1).max(900),
    source: AvatarArtifactSourceSchema,
    manifest: AvatarArtifactManifestObjectSchema,
    renderer: AvatarRendererIdentitySchema,
})
    .strict();
/** Historical immutable binding used only to keep pre-v4 jobs readable. */
export const AvatarArtifactLegacyRuntimeBindingSchema = AvatarArtifactLegacyRuntimeBindingObjectSchema.superRefine(validateArtifactScope);
/** Immutable manifest-v3 snapshot copied into every current render job. */
export const AvatarArtifactRuntimeBindingSchema = AvatarArtifactLegacyRuntimeBindingObjectSchema.extend({
    manifestSchemaVersion: z.literal(3),
}).superRefine(validateArtifactScope);
export const AvatarProfileSchema = z
    .object({
    tenantId: z.string().uuid(),
    profileId: AvatarProfileIdSchema,
    lifecycleStatus: z.enum(['ready', 'deleting']),
    activeArtifactId: z.string().uuid().nullable(),
    activeArtifactActivatedAt: TimestampSchema.nullable(),
    activeArtifactActivatedBy: CognitoSubSchema.nullable(),
    revision: z.number().int().nonnegative(),
    createdAt: TimestampSchema,
    createdBy: CognitoSubSchema,
    updatedAt: TimestampSchema,
    updatedBy: CognitoSubSchema,
    deletionRequestedAt: TimestampSchema.optional(),
    deletionRequestedBy: CognitoSubSchema.optional(),
})
    .strict()
    .superRefine((profile, ctx) => {
    const activeValues = [
        profile.activeArtifactId,
        profile.activeArtifactActivatedAt,
        profile.activeArtifactActivatedBy,
    ];
    if (!activeValues.every((value) => (activeValues[0] === null ? value === null : value !== null))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activeArtifactId'],
            message: 'Active artifact identity and activation metadata must be all null or all set.',
        });
    }
    const deletionFields = [
        profile.deletionRequestedAt !== undefined,
        profile.deletionRequestedBy !== undefined,
    ];
    if ((profile.lifecycleStatus === 'deleting' && !deletionFields.every(Boolean)) ||
        (profile.lifecycleStatus === 'ready' && deletionFields.some(Boolean))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['deletionRequestedAt'],
            message: 'Profile deletion metadata must match its lifecycle state.',
        });
    }
});
const AvatarArtifactObjectSchema = z
    .object({
    tenantId: z.string().uuid(),
    profileId: AvatarProfileIdSchema,
    artifactId: z.string().uuid(),
    source: AvatarArtifactSourceSchema,
    status: AvatarArtifactStatusSchema,
    renderer: AvatarRendererIdentitySchema,
    artifactS3Prefix: z.string().min(1).max(900),
    manifest: AvatarArtifactManifestObjectSchema.optional(),
    /** Optional only so historical artifact rows remain readable during migration. */
    manifestSchemaVersion: z.union([z.literal(2), z.literal(3)]).optional(),
    reviewPreview: AvatarArtifactPreviewObjectSchema.optional(),
    qualityReview: AvatarArtifactQualityReviewSchema.optional(),
    revision: z.number().int().nonnegative(),
    createdAt: TimestampSchema,
    createdBy: CognitoSubSchema,
    updatedAt: TimestampSchema,
    updatedBy: AvatarArtifactActorSchema,
    preparationStartedAt: TimestampSchema.optional(),
    preparationStartedBy: AvatarArtifactActorSchema.optional(),
    activatedAt: TimestampSchema.optional(),
    activatedBy: CognitoSubSchema.optional(),
    retiredAt: TimestampSchema.optional(),
    retiredBy: CognitoSubSchema.optional(),
    purgeNotBefore: TimestampSchema.optional(),
    failureCode: z.string().min(1).max(80).optional(),
    deletionRequestedAt: TimestampSchema.optional(),
    deletionRequestedBy: CognitoSubSchema.optional(),
})
    .strict();
export const AvatarArtifactSchema = AvatarArtifactObjectSchema.superRefine((artifact, ctx) => {
    validateArtifactScope(artifact, ctx);
    if (artifact.qualityReview) {
        const preview = artifact.reviewPreview;
        if (preview === undefined ||
            artifact.qualityReview.preview.bucket !== preview.bucket ||
            artifact.qualityReview.preview.key !== preview.key ||
            artifact.qualityReview.preview.versionId !== preview.versionId ||
            artifact.qualityReview.preview.etag !== preview.etag ||
            artifact.qualityReview.preview.bytes !== preview.bytes ||
            artifact.qualityReview.preview.sha256 !== preview.sha256 ||
            artifact.qualityReview.preview.contentType !== preview.contentType) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['qualityReview', 'preview'],
                message: 'Technical review must bind the exact worker-produced preview.',
            });
        }
        if (artifact.manifest === undefined ||
            artifact.qualityReview.sourceSha256 !== artifact.source.object.sha256 ||
            artifact.qualityReview.manifestSha256 !== artifact.manifest.sha256 ||
            artifact.qualityReview.rendererImageDigest !== artifact.renderer.rendererImageDigest ||
            artifact.qualityReview.modelDigest !== artifact.renderer.modelDigest) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['qualityReview'],
                message: 'Technical review digests must match the prepared artifact.',
            });
        }
    }
    const preparationFields = [
        artifact.preparationStartedAt !== undefined,
        artifact.preparationStartedBy !== undefined,
    ];
    if (!preparationFields.every((present) => present === preparationFields[0])) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['preparationStartedAt'],
            message: 'Preparation timestamp and actor must appear together.',
        });
    }
    const preparationRequired = artifact.status === 'preparing' ||
        artifact.status === 'review' ||
        artifact.status === 'active' ||
        artifact.status === 'retired';
    if (preparationRequired && !preparationFields.every(Boolean)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['preparationStartedAt'],
            message: 'Prepared artifact states require preparation provenance.',
        });
    }
    if (artifact.status === 'queued' && preparationFields.some(Boolean)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['preparationStartedAt'],
            message: 'A queued artifact cannot carry preparation provenance.',
        });
    }
    const prepared = artifact.status === 'review' || artifact.status === 'active' || artifact.status === 'retired';
    if (prepared && (artifact.manifest === undefined || artifact.reviewPreview === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['manifest'],
            message: 'Reviewable, active, and retired artifacts require an exact manifest and preview.',
        });
    }
    if (artifact.manifest === undefined && artifact.manifestSchemaVersion !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['manifestSchemaVersion'],
            message: 'A manifest schema version requires an exact manifest object.',
        });
    }
    if ((artifact.status === 'queued' || artifact.status === 'preparing') &&
        (artifact.manifest !== undefined ||
            artifact.manifestSchemaVersion !== undefined ||
            artifact.reviewPreview !== undefined ||
            artifact.qualityReview !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['manifest'],
            message: 'Queued and preparing artifacts cannot carry review outputs.',
        });
    }
    const activationFields = [artifact.activatedAt !== undefined, artifact.activatedBy !== undefined];
    if (!activationFields.every((present) => present === activationFields[0])) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activatedAt'],
            message: 'Activation timestamp and actor must appear together.',
        });
    }
    const activated = artifact.status === 'active' || artifact.status === 'retired';
    if (activated) {
        if (artifact.qualityReview?.decision !== 'approved' || artifact.manifest === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['qualityReview'],
                message: 'Active or retired artifacts require an approved technical review.',
            });
        }
        if (!activationFields.every(Boolean)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['activatedAt'],
                message: 'Active or retired artifacts require activation metadata.',
            });
        }
    }
    if (!activated && artifact.status !== 'deleting' && activationFields.some(Boolean)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['activatedAt'],
            message: 'Only active, retired, or deleting artifacts can carry activation metadata.',
        });
    }
    const retirementFields = [
        artifact.retiredAt !== undefined,
        artifact.retiredBy !== undefined,
        artifact.purgeNotBefore !== undefined,
    ];
    if (artifact.status === 'retired' && !retirementFields.every(Boolean)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retiredAt'],
            message: 'Retired artifacts require retirement and retention metadata.',
        });
    }
    if (artifact.status !== 'retired' &&
        artifact.status !== 'deleting' &&
        retirementFields.some(Boolean)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retiredAt'],
            message: 'Only retired or deleting artifacts can carry retirement metadata.',
        });
    }
    if (artifact.status === 'deleting' &&
        retirementFields.some(Boolean) &&
        !retirementFields.every(Boolean)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['retiredAt'],
            message: 'Deleting artifacts must retain complete retirement metadata.',
        });
    }
    if ((artifact.status === 'failed') !== (artifact.failureCode !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['failureCode'],
            message: 'Only failed artifacts carry a preparation failure code.',
        });
    }
    const deletionFields = [
        artifact.deletionRequestedAt !== undefined,
        artifact.deletionRequestedBy !== undefined,
    ];
    if ((artifact.status === 'deleting' && !deletionFields.every(Boolean)) ||
        (artifact.status !== 'deleting' && deletionFields.some(Boolean))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['deletionRequestedAt'],
            message: 'Artifact deletion metadata must match its lifecycle state.',
        });
    }
});
export function avatarArtifactRuntimeBinding(artifact) {
    if (artifact.status !== 'active' ||
        artifact.manifest === undefined ||
        artifact.manifestSchemaVersion !== 3) {
        throw new Error('Only an active manifest-v3 avatar artifact can be bound to a render job.');
    }
    return AvatarArtifactRuntimeBindingSchema.parse({
        tenantId: artifact.tenantId,
        profileId: artifact.profileId,
        artifactId: artifact.artifactId,
        artifactS3Prefix: artifact.artifactS3Prefix,
        source: artifact.source,
        manifest: artifact.manifest,
        manifestSchemaVersion: artifact.manifestSchemaVersion,
        renderer: artifact.renderer,
    });
}
const PointSchema = z.tuple([z.number().finite(), z.number().finite()]);
const SourceFramesFileV2Schema = z
    .object({
    role: z.literal('source-frames-bgr24'),
    relativePath: z.literal('source-frames.bgr24'),
    object: AvatarVersionedObjectSchema.extend({
        bytes: z.literal(155_520_000),
        contentType: z.literal('application/octet-stream'),
    }),
})
    .strict();
const SourceFramesFileV3Schema = z
    .object({
    role: z.literal('source-frames-bgr24'),
    relativePath: z.literal('source-frames.bgr24'),
    object: AvatarVersionedObjectSchema.extend({
        bytes: z.literal(777_600_000),
        contentType: z.literal('application/octet-stream'),
    }),
})
    .strict();
const AvatarArtifactManifestV2Fields = {
    schemaVersion: z.literal(2),
    kind: z.literal('cloneai-latentsync-avatar-artifact'),
    tenantId: z.string().uuid(),
    profileId: AvatarProfileIdSchema,
    artifactId: z.string().uuid(),
    createdAt: TimestampSchema,
    release: z
        .object({
        tier: z.literal('controlled-pilot'),
        productionCleared: z.literal(false),
    })
        .strict(),
    source: AvatarArtifactSourceSchema,
    renderer: AvatarRendererIdentitySchema,
    artifactBucket: z.string().min(3).max(63),
    artifactS3Prefix: z.string().min(1).max(900),
    media: z
        .object({
        sourceWindowStartMicros: z.number().int().nonnegative().max(3_600_000_000),
        sourceWindowDurationMicros: z.literal(1_000_000),
        width: z.literal(1_080),
        height: z.literal(1_920),
        fpsNumerator: z.literal(25),
        fpsDenominator: z.literal(1),
        frameCount: z.literal(25),
        pixelFormat: z.literal('bgr24'),
        frameLayout: z.literal('frame-major-row-major-top-down-interleaved-bgr24-v1'),
        sourceFrameSchedule: z.literal('loop-forward-v1'),
    })
        .strict(),
    geometry: z
        .object({
        policyId: z.literal('fixed-reviewed-face-box-ratios-v1'),
        faceBox: z.tuple([
            z.number().int().nonnegative(),
            z.number().int().nonnegative(),
            z.number().int().positive(),
            z.number().int().positive(),
        ]),
        landmarkRatios: z
            .object({
            leftEyebrowCenter: PointSchema,
            rightEyebrowCenter: PointSchema,
            noseCenter: PointSchema,
        })
            .strict(),
        alignedCanvas: z
            .object({
            width: z.literal(420),
            height: z.literal(560),
            modelResolution: z.literal(512),
            targetLandmarks: z.tuple([
                z.tuple([z.literal(95.2), z.literal(112)]),
                z.tuple([z.literal(324.8), z.literal(112)]),
                z.tuple([z.literal(210), z.literal(224)]),
            ]),
        })
            .strict(),
    })
        .strict(),
    inference: z
        .object({
        seed: z.literal(1_247),
        steps: z.literal(35),
        guidanceScale: z.literal(1.5),
        batchFrameLimit: z.literal(16),
        mask: z.literal('latentsync-stock-mask'),
        preserveSourcePixelsOutsideMask: z.literal(true),
    })
        .strict(),
    files: z.tuple([SourceFramesFileV2Schema]),
};
const AvatarArtifactManifestV3Fields = {
    ...AvatarArtifactManifestV2Fields,
    schemaVersion: z.literal(3),
    media: z
        .object({
        sourceWindowStartMicros: z.number().int().nonnegative().max(3_600_000_000),
        sourceWindowDurationMicros: z.literal(5_000_000),
        width: z.literal(1_080),
        height: z.literal(1_920),
        fpsNumerator: z.literal(25),
        fpsDenominator: z.literal(1),
        frameCount: z.literal(125),
        pixelFormat: z.literal('bgr24'),
        frameLayout: z.literal('frame-major-row-major-top-down-interleaved-bgr24-v1'),
        sourceFrameSchedule: z.literal('single-pass-forward-v1'),
    })
        .strict(),
    files: z.tuple([SourceFramesFileV3Schema]),
};
const AvatarArtifactBundleDigestDocumentV2ObjectSchema = z
    .object(AvatarArtifactManifestV2Fields)
    .strict();
const AvatarArtifactBundleDigestDocumentV3ObjectSchema = z
    .object(AvatarArtifactManifestV3Fields)
    .strict();
function validateAvatarArtifactManifest(manifest, ctx) {
    const prefix = expectedArtifactPrefix(manifest);
    if (manifest.artifactS3Prefix !== prefix) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['artifactS3Prefix'],
            message: 'Manifest artifact prefix does not match its tenant and identity.',
        });
    }
    for (const [index, file] of manifest.files.entries()) {
        if (file.object.bucket !== manifest.artifactBucket) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['files', index, 'object', 'bucket'],
                message: 'Every prepared payload must use the manifest artifact bucket.',
            });
        }
        if (file.object.key !== `${prefix}${file.relativePath}`) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['files', index, 'object', 'key'],
                message: 'Prepared payload key does not match its exact artifact-scoped path.',
            });
        }
    }
    const [x0, y0, x1, y1] = manifest.geometry.faceBox;
    if (x1 <= x0 || y1 <= y0 || x1 > manifest.media.width || y1 > manifest.media.height) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['geometry', 'faceBox'],
            message: 'Face box must be ordered and contained by the source frame.',
        });
    }
    for (const [name, point] of Object.entries(manifest.geometry.landmarkRatios)) {
        if (point.some((coordinate) => coordinate < 0 || coordinate > 1)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['geometry', 'landmarkRatios', name],
                message: 'Landmark ratios must be in the inclusive [0, 1] range.',
            });
        }
    }
    const paths = manifest.files.map((file) => file.relativePath);
    if (new Set(paths).size !== paths.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['files'],
            message: 'Artifact file paths must be unique.',
        });
    }
}
export const AvatarArtifactBundleDigestDocumentV2Schema = AvatarArtifactBundleDigestDocumentV2ObjectSchema.superRefine(validateAvatarArtifactManifest);
export const AvatarArtifactBundleDigestDocumentV3Schema = AvatarArtifactBundleDigestDocumentV3ObjectSchema.superRefine(validateAvatarArtifactManifest);
export const AvatarArtifactBundleDigestDocumentSchema = z.union([
    AvatarArtifactBundleDigestDocumentV2Schema,
    AvatarArtifactBundleDigestDocumentV3Schema,
]);
const AvatarArtifactManifestV2ObjectSchema = z
    .object({
    ...AvatarArtifactManifestV2Fields,
    bundleDigestSha256: Sha256HexSchema,
})
    .strict();
const AvatarArtifactManifestV3ObjectSchema = z
    .object({
    ...AvatarArtifactManifestV3Fields,
    bundleDigestSha256: Sha256HexSchema,
})
    .strict();
export const AvatarArtifactManifestV2Schema = AvatarArtifactManifestV2ObjectSchema.superRefine(validateAvatarArtifactManifest);
export const AvatarArtifactManifestV3Schema = AvatarArtifactManifestV3ObjectSchema.superRefine(validateAvatarArtifactManifest);
export const AvatarArtifactManifestSchema = z.union([
    AvatarArtifactManifestV2Schema,
    AvatarArtifactManifestV3Schema,
]);
export const AVATAR_ARTIFACT_BUNDLE_DIGEST_DOMAIN_V2 = 'cloneai-latentsync-avatar-artifact-bundle-v2';
export const AVATAR_ARTIFACT_BUNDLE_DIGEST_DOMAIN_V3 = 'cloneai-latentsync-avatar-artifact-bundle-v3';
/** Historical alias retained for callers that validate v2 receipts. */
export const AVATAR_ARTIFACT_BUNDLE_DIGEST_DOMAIN = AVATAR_ARTIFACT_BUNDLE_DIGEST_DOMAIN_V2;
export const AVATAR_ARTIFACT_CANONICAL_JSON_ALGORITHM = 'cloneai-canonical-typed-json-f64-v1';
function utf8Hex(value) {
    let result = '';
    const appendByte = (byte) => {
        result += byte.toString(16).padStart(2, '0');
    };
    for (let index = 0; index < value.length; index += 1) {
        let codePoint = value.charCodeAt(index);
        if (codePoint >= 0xd800 && codePoint <= 0xdbff) {
            const low = value.charCodeAt(index + 1);
            if (low < 0xdc00 || low > 0xdfff) {
                throw new TypeError('Canonical artifact JSON requires well-formed Unicode strings.');
            }
            codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (low - 0xdc00);
            index += 1;
        }
        else if (codePoint >= 0xdc00 && codePoint <= 0xdfff) {
            throw new TypeError('Canonical artifact JSON requires well-formed Unicode strings.');
        }
        if (codePoint <= 0x7f) {
            appendByte(codePoint);
        }
        else if (codePoint <= 0x7ff) {
            appendByte(0xc0 | (codePoint >> 6));
            appendByte(0x80 | (codePoint & 0x3f));
        }
        else if (codePoint <= 0xffff) {
            appendByte(0xe0 | (codePoint >> 12));
            appendByte(0x80 | ((codePoint >> 6) & 0x3f));
            appendByte(0x80 | (codePoint & 0x3f));
        }
        else {
            appendByte(0xf0 | (codePoint >> 18));
            appendByte(0x80 | ((codePoint >> 12) & 0x3f));
            appendByte(0x80 | ((codePoint >> 6) & 0x3f));
            appendByte(0x80 | (codePoint & 0x3f));
        }
    }
    return result;
}
function float64Hex(value) {
    if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) {
        throw new TypeError('Canonical artifact JSON requires finite safe JSON numbers.');
    }
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setFloat64(0, Object.is(value, -0) ? 0 : value, false);
    let result = '';
    for (let index = 0; index < 8; index += 1) {
        result += view.getUint8(index).toString(16).padStart(2, '0');
    }
    return result;
}
function canonicalArtifactJsonNode(value) {
    if (value === null)
        return ['null'];
    if (typeof value === 'boolean')
        return ['boolean', value];
    if (typeof value === 'number')
        return ['number-f64-be', float64Hex(value)];
    if (typeof value === 'string')
        return ['string-utf8', utf8Hex(value)];
    if (Array.isArray(value)) {
        return ['array', value.map((item) => canonicalArtifactJsonNode(item))];
    }
    if (typeof value !== 'object') {
        throw new TypeError('Canonical artifact JSON accepts only JSON values.');
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new TypeError('Canonical artifact JSON accepts only JSON objects.');
    }
    if (Object.getOwnPropertySymbols(value).length > 0) {
        throw new TypeError('Canonical artifact JSON does not accept symbol keys.');
    }
    const entries = Object.entries(value).map(([key, item]) => [
        utf8Hex(key),
        canonicalArtifactJsonNode(item),
    ]);
    entries.sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
    return ['object', entries];
}
/**
 * Canonical typed JSON used only for immutable avatar bundle digests.
 *
 * Strings and object keys are lowercase UTF-8 hex; finite safe numbers are
 * big-endian IEEE-754 binary64 hex with negative zero normalized to zero; and
 * object entries sort by UTF-8 bytes. The projected tree contains only arrays,
 * booleans, and ASCII strings, so compact JSON serialization is identical in
 * JavaScript and Python.
 */
export function canonicalAvatarArtifactBundleJson(value) {
    return JSON.stringify(canonicalArtifactJsonNode(value));
}
/**
 * Returns the exact UTF-8 text whose SHA-256 is `bundleDigestSha256`.
 * Accepts either a complete manifest or its pre-digest document. The digest
 * field is structurally absent from the canonical typed JSON, so a bundle
 * never hashes itself.
 */
export function avatarArtifactBundleDigestInput(manifestOrDocument) {
    const parsedManifest = AvatarArtifactManifestSchema.safeParse(manifestOrDocument);
    let document;
    if (parsedManifest.success) {
        const { bundleDigestSha256: _bundleDigestSha256, ...digestDocument } = parsedManifest.data;
        document = AvatarArtifactBundleDigestDocumentSchema.parse(digestDocument);
    }
    else {
        document = AvatarArtifactBundleDigestDocumentSchema.parse(manifestOrDocument);
    }
    const domain = document.schemaVersion === 2
        ? AVATAR_ARTIFACT_BUNDLE_DIGEST_DOMAIN_V2
        : AVATAR_ARTIFACT_BUNDLE_DIGEST_DOMAIN_V3;
    return `${domain}\n${canonicalAvatarArtifactBundleJson(document)}\n`;
}
/** Parse a manifest body and bind its storage coordinates to the immutable job snapshot. */
export function avatarArtifactManifestForObject(manifest, manifestObject) {
    const parsedManifest = AvatarArtifactManifestSchema.parse(manifest);
    const parsedObject = AvatarArtifactManifestObjectSchema.parse(manifestObject);
    if (parsedManifest.artifactBucket !== parsedObject.bucket ||
        parsedObject.key !== `${parsedManifest.artifactS3Prefix}manifest.json`) {
        throw new Error('Prepared avatar manifest does not match its immutable object binding.');
    }
    return parsedManifest;
}
//# sourceMappingURL=avatarProfile.js.map